import {
  DEFAULT_AGE_QUOTA_CRITERIA,
  FeasibilityCriterion,
  FeasibilityQuota,
  QUALIFICATION_CODE,
  Survey,
  SurveyQualification,
} from '@ps/survey-utils';
import {
  FeasibilityCriteriaDto,
  FeasibilityRequestDto,
} from '../dtos/feasibility.dto';

export interface FeasibilityPayloadResult {
  readonly request: FeasibilityRequestDto;
  readonly defaultQuotaCriteria: readonly FeasibilityCriterion[];
  readonly quotaSet: ReadonlyMap<number | string, readonly FeasibilityQuota[]>;
}

// TODO: Update this once `Survey.quotas` is added
export class FeasibilityPayloadMapper {
  public build(survey: Survey, userId: number): FeasibilityPayloadResult {
    const defaultQuotaCriteria = this._buildDefaultQuotaCriteria(
      survey.qualifications ?? [],
    );

    const ageQuotaId = crypto.randomUUID();
    const requestCriteria = defaultQuotaCriteria.map((criterion) =>
      this._toDto(criterion),
    );

    const request: FeasibilityRequestDto = {
      buyer_id: survey.buyerCompanyId,
      user_id: userId,
      project_id: survey.id,
      completes_required: survey.completesRequired,
      expected_ir: survey.expectedIr,
      expected_loi: survey.expectedLoi,
      field_time: survey.fieldTime,
      survey_localization: `${survey.languageCode}_${survey.countryCode}`,
      quotas: [
        {
          buyer_quota_id: ageQuotaId,
          required_count: survey.completesRequired,
          criteria: this._trimZipcode(requestCriteria, {
            clearConditionCodes: false,
          }),
        },
      ],
      defaultQuota: [
        {
          criteria: this._trimZipcode(requestCriteria, {
            clearConditionCodes: true,
          }),
        },
      ],
      offer_price: { value: null, active: false },
      survey_grouping: [],
      is_exclusion: false,
      grouping_statuses: [],
      click_balancing: survey.clickBalancing,
      is_optimized_payload: true,
    };

    // TODO: Update this once `Survey.quotas` is added.
    const quotaSet = new Map<number | string, FeasibilityQuota[]>();

    return { request, defaultQuotaCriteria, quotaSet };
  }

  private _buildDefaultQuotaCriteria(
    qualifications: readonly SurveyQualification[],
  ): FeasibilityCriterion[] {
    const criteria = qualifications
      .filter((qualification) => !qualification.isDefault)
      .filter((qualification) => qualification.qualificationName !== 'Location')
      .map((qualification) => this._toCriterion(qualification));

    const hasAgeCriterion = criteria.some(
      (criterion) => criterion.qualificationCode === QUALIFICATION_CODE.AGE,
    );

    return hasAgeCriterion
      ? criteria
      : [...criteria, this._defaultAgeCriterion()];
  }

  private _toCriterion(
    qualification: SurveyQualification,
  ): FeasibilityCriterion {
    if (qualification.isRangeType) {
      return {
        qualificationCode: qualification.qualificationCode,
        qualificationName: qualification.qualificationName,
        rangeSets: qualification.rangeSets,
      };
    }

    return {
      qualificationCode: qualification.qualificationCode,
      qualificationName: qualification.qualificationName,
      conditionCodes: qualification.conditionCodes,
      conditionNames: qualification.data.map((datum) => datum.name),
    };
  }

  private _defaultAgeCriterion(): FeasibilityCriterion {
    return {
      qualificationCode: QUALIFICATION_CODE.AGE,
      qualificationName: DEFAULT_AGE_QUOTA_CRITERIA.NAME,
      rangeSets: [
        {
          from: DEFAULT_AGE_QUOTA_CRITERIA.MIN,
          to: DEFAULT_AGE_QUOTA_CRITERIA.MAX,
          units: DEFAULT_AGE_QUOTA_CRITERIA.UNITS,
        },
      ],
    };
  }

  private _toDto(criterion: FeasibilityCriterion): FeasibilityCriteriaDto {
    return {
      qualification_code: criterion.qualificationCode,
      qualification_name: criterion.qualificationName,
      condition_codes: criterion.conditionCodes
        ? [...criterion.conditionCodes]
        : undefined,
      condition_names: criterion.conditionNames
        ? [...criterion.conditionNames]
        : undefined,
      range_sets: criterion.rangeSets?.map((range) => ({ ...range })),
    };
  }

  /**
   * ZIPCODE lists can be large, so this mapper strips them before sending: always
   * from `defaultQuota` (display-only), and from `quotas` only when already
   * present.
   */
  private _trimZipcode(
    criteria: readonly FeasibilityCriteriaDto[],
    { clearConditionCodes }: { clearConditionCodes: boolean },
  ): FeasibilityCriteriaDto[] {
    return criteria.map((criterion) => {
      if (
        criterion.qualification_code !== QUALIFICATION_CODE.ZIPCODE ||
        (!clearConditionCodes && !criterion.condition_names)
      ) {
        return criterion;
      }

      return {
        ...criterion,
        condition_names: [],
        ...(clearConditionCodes ? { condition_codes: [] } : {}),
      };
    });
  }
}
